import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Translate, withLocalize } from 'react-localize-redux';
import css from './style.css';
import XIcon from '../../resources/svg/icon_remove_row.svg';
import Button from '../../components/atoms/Button';
import CheckMark from '../../resources/icons/check_mark.svg';
import {
    setPanelShownClasses,
    trackTrialStatusCloseClick,
    trackTrialStatusCTAClick,
    STATUS_PANEL_CLOSED_DATE,
    trackTrialCTAClick,
} from '../utils';
import { getIsTrialUser, getTrialRealDaysLeft, getUserData, shouldShowTrialStatusPanel } from '../../selectors/user';
import { toggleTrialStatusPanel } from '../../actions/user';

const PANEL_CONTAINER_ID = 'trial-status-panel-container';
const PANEL_SHOWN_CLASS = 'trial-status-panel-shown';
const SHOW_PANEL_MAX_DAYS_LEFT = 15;
const ALERT_MAX_DAYS_LEFT = 7;

function TrialStatusPanel({ translate }) {
    const dispatch = useDispatch();
    const requested = useSelector(shouldShowTrialStatusPanel);
    const isTrialUser = useSelector(getIsTrialUser);
    const trialRealDaysLeft = useSelector(getTrialRealDaysLeft);
    const userData = useSelector(getUserData);
    const [upgradeClicked, setUpgradeClicked] = useState(false);

    const visible =
        requested &&
        isTrialUser &&
        typeof trialRealDaysLeft === 'number' &&
        trialRealDaysLeft <= SHOW_PANEL_MAX_DAYS_LEFT;
    const panelContainer = visible ? document.getElementById(PANEL_CONTAINER_ID) : null;

    useEffect(() => {
        if (!panelContainer) return undefined;
        setPanelShownClasses(PANEL_CONTAINER_ID, PANEL_SHOWN_CLASS, true);
        return () => setPanelShownClasses(PANEL_CONTAINER_ID, PANEL_SHOWN_CLASS, false);
    }, [panelContainer]);

    if (!panelContainer) {
        return null;
    }

    const onClose = () => {
        trackTrialStatusCloseClick(trialRealDaysLeft);
        localStorage.setItem(STATUS_PANEL_CLOSED_DATE, new Date().toDateString());
        dispatch(toggleTrialStatusPanel(false));
    };

    const onUpgrade = () => {
        trackTrialCTAClick(userData.email, userData.first_name, userData.last_name, userData.company);
        trackTrialStatusCTAClick(trialRealDaysLeft);
        setUpgradeClicked(true);
    };

    const panel = upgradeClicked ? (
        <div role="status" className={classNames(css.trialStatusPanel, css.upgraded)}>
            <CheckMark className={css.checkmark} />
            <div className={css.thankYouLabel}>
                <Translate id="STATIC.PAGES.TRIAL_STATUS_PANEL.PANEL_UPGRADED_TEXT" />
            </div>
        </div>
    ) : (
        <div
            role="status"
            className={classNames(css.trialStatusPanel, {
                [css.alert]: trialRealDaysLeft <= ALERT_MAX_DAYS_LEFT,
                [css.warning]: trialRealDaysLeft > ALERT_MAX_DAYS_LEFT,
            })}
        >
            <div className={css.label}>
                <Translate id="STATIC.PAGES.TRIAL_STATUS_PANEL.PANEL_TEXT" />
            </div>
            <Button
                type="link"
                className={css.upgradeButton}
                text="STATIC.PAGES.TRIAL_STATUS_PANEL.PANEL_OPEN_DRIFT_BUTTON"
                onClick={onUpgrade}
            />
            <button
                type="button"
                className={css.closeButton}
                aria-label={translate('STATIC.BUTTONS.CLOSE')}
                onClick={onClose}
            >
                <XIcon className={css.XIcon} />
            </button>
        </div>
    );

    return ReactDOM.createPortal(panel, panelContainer);
}

TrialStatusPanel.propTypes = {
    translate: PropTypes.func.isRequired,
};

export default withLocalize(TrialStatusPanel);
